const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Use mock endpoint for development (no auth required)
const USE_MOCK = true;

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions?: string[];
  tabPermissions: string[];
  isSystemRole: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Tab {
  id: string;
  name: string;
  description: string;
}

export interface CreateRoleData {
  name: string;
  description: string;
  permissions?: string[];
  tabPermissions: string[];
}

export interface UpdateRoleData {
  name?: string;
  description?: string;
  permissions?: string[];
  tabPermissions?: string[];
}

class RolesService {
  private baseURL = `${API_BASE_URL}/roles`; // Use real API

  // Helper to get auth headers
  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken'); // Fixed token key
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Get all roles
  async getAllRoles(): Promise<Role[]> {
    try {
      const response = await fetch(this.baseURL, {
        headers: this.getAuthHeaders()
      });
      const data = await response.json();
      return data.roles || [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  // Get role by ID
  async getRoleById(id: number): Promise<Role | null> {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        headers: this.getAuthHeaders()
      });
      const data = await response.json();
      return data.role || null;
    } catch (error) {
      console.error('Error fetching role:', error);
      throw error;
    }
  }

  // Create new role
  async createRole(roleData: CreateRoleData): Promise<Role> {
    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(roleData)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create role');
      }
      return data.role;
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }

  // Update role
  async updateRole(id: number, roleData: UpdateRoleData): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(roleData)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  // Delete role
  async deleteRole(id: number): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }

  // Get available tabs
  async getAvailableTabs(): Promise<Tab[]> {
    try {
      const response = await fetch(`${this.baseURL}/tabs/available`, {
        headers: this.getAuthHeaders()
      });
      const data = await response.json();
      return data.tabs || [];
    } catch (error) {
      console.error('Error fetching available tabs:', error);
      throw error;
    }
  }
}

export default new RolesService();
